import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import supportService from '../services/supportService';
import { SupportTicket, TicketStatus, TicketPriority, SupportNote, SupportContact } from '../types/support-ticket';
import { formatDistanceToNow, format } from 'date-fns';
import { Search, Plus, Filter, Clock, AlertCircle, CheckCircle, User, Tag, RefreshCw } from 'lucide-react';
import TicketProperties from '../components/support/TicketProperties';

// Define badge variant types to match the ones available in the UI library
type BadgeVariant = "default" | "secondary" | "warning" | "outline" | "success" | "destructive" | "info";

const SupportTickets = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [ticketNotes, setTicketNotes] = useState<SupportNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState<TicketStatus | 'all'>('all');
  const [newNote, setNewNote] = useState('');
  const [isPrivateNote, setIsPrivateNote] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [contacts, setContacts] = useState<SupportContact[]>([]);
  
  // Load tickets
  useEffect(() => {
    const loadTickets = async () => {
      setLoading(true);
      try {
        const allTickets = await supportService.getTickets();
        setTickets(allTickets);
        setFilteredTickets(allTickets);
        
        // Também carrega contatos para exibir informações do cliente
        const allContacts = await supportService.getContacts();
        setContacts(allContacts);
      } catch (error) {
        console.error('Failed to load tickets', error);
      } finally {
        setLoading(false);
      }
    };

    loadTickets();
  }, []);

  // Filter tickets based on active tab and search term
  useEffect(() => {
    let filtered = [...tickets];

    // Filter by status
    if (activeTab !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === activeTab);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(ticket => 
        ticket.subject.toLowerCase().includes(term) || 
        ticket.description.toLowerCase().includes(term)
      );
    }

    setFilteredTickets(filtered);
  }, [tickets, activeTab, searchTerm]);

  // Load ticket notes when a ticket is selected
  useEffect(() => {
    if (!selectedTicket) {
      setTicketNotes([]);
      return;
    }

    const loadNotes = async () => {
      setLoadingNotes(true);
      try {
        const notes = await supportService.getNotesByTicketId(selectedTicket.id);
        setTicketNotes(notes);
      } catch (error) {
        console.error('Failed to load notes', error);
      } finally {
        setLoadingNotes(false);
      }
    };

    loadNotes();
  }, [selectedTicket]);

  const handleSelectTicket = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
  };

  const handleUpdateStatus = async (status: TicketStatus) => {
    if (!selectedTicket) return;

    try {
      const updatedTicket = await supportService.updateTicket(selectedTicket.id, { status });
      if (updatedTicket) {
        // Update in the tickets list
        const updatedTickets = tickets.map(ticket => 
          ticket.id === updatedTicket.id ? updatedTicket : ticket
        );
        setTickets(updatedTickets);
        setSelectedTicket(updatedTicket);
        
        // Se o status for atualizado para resolvido, adiciona automaticamente uma nota
        if (status === 'solved' && selectedTicket.status !== 'solved') {
          await handleAddSystemNote(`Ticket marcado como resolvido`);
        }
      }
    } catch (error) {
      console.error('Failed to update ticket status', error);
    }
  };

  const handleUpdatePriority = async (priority: TicketPriority) => {
    if (!selectedTicket) return;

    try {
      const updatedTicket = await supportService.updateTicket(selectedTicket.id, { priority });
      if (updatedTicket) {
        // Update in the tickets list
        const updatedTickets = tickets.map(ticket => 
          ticket.id === updatedTicket.id ? updatedTicket : ticket
        );
        setTickets(updatedTickets);
        setSelectedTicket(updatedTicket);
        
        // Adiciona uma nota sobre a alteração de prioridade
        await handleAddSystemNote(`Prioridade alterada para ${priority}`);
      }
    } catch (error) {
      console.error('Failed to update ticket priority', error);
    }
  };

  const handleAddNote = async () => {
    if (!selectedTicket || !newNote.trim()) return;

    try {
      const note = await supportService.createNote({
        ticket_id: selectedTicket.id,
        author_id: 'agent-1', // In a real app, this would be the logged-in agent's ID
        content: newNote,
        is_private: isPrivateNote
      });

      setTicketNotes([note, ...ticketNotes]);
      setNewNote('');
    } catch (error) {
      console.error('Failed to add note', error);
    }
  };
  
  const handleAddSystemNote = async (content: string) => {
    if (!selectedTicket) return;
    
    try {
      const note = await supportService.createNote({
        ticket_id: selectedTicket.id,
        author_id: 'system',
        content: content,
        is_private: true
      });
      
      setTicketNotes([note, ...ticketNotes]);
    } catch (error) {
      console.error('Failed to add system note', error);
    }
  };

  const handleResolveTicket = async () => {
    if (!selectedTicket) return;

    try {
      const resolvedTicket = await supportService.updateTicket(selectedTicket.id, {
        status: 'solved',
        resolution: 'Problema resolvido por agente de suporte',
        resolution_date: new Date().toISOString()
      });

      if (resolvedTicket) {
        // Update in the tickets list
        const updatedTickets = tickets.map(ticket => 
          ticket.id === resolvedTicket.id ? resolvedTicket : ticket
        );
        setTickets(updatedTickets);
        setSelectedTicket(resolvedTicket);
        
        // Adiciona uma nota sobre a resolução
        await handleAddSystemNote('Ticket marcado como resolvido');
      }
    } catch (error) {
      console.error('Failed to resolve ticket', error);
    }
  };
  
  const getContactById = (id: string) => {
    return contacts.find(contact => contact.id === id);
  };

  const getStatusBadgeVariant = (status: TicketStatus): BadgeVariant => {
    switch (status) {
      case 'new':
        return 'default';
      case 'open':
        return 'secondary';
      case 'pending':
        return 'warning';
      case 'on_hold':
        return 'outline';
      case 'solved':
        return 'success';
      case 'closed':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const getPriorityBadgeVariant = (priority: TicketPriority): BadgeVariant => {
    switch (priority) {
      case 'low':
        return 'outline';
      case 'medium':
        return 'secondary';
      case 'high':
        return 'warning';
      case 'urgent':
        return 'destructive';
      default:
        return 'default';
    }
  };
  
  const getStatusLabel = (status: TicketStatus): string => {
    switch (status) {
      case 'new': return 'Novo';
      case 'open': return 'Aberto';
      case 'pending': return 'Pendente';
      case 'on_hold': return 'Em espera';
      case 'solved': return 'Resolvido';
      case 'closed': return 'Fechado';
      default: return status;
    }
  };
  
  const getPriorityLabel = (priority: TicketPriority): string => {
    switch (priority) {
      case 'low': return 'Baixa';
      case 'medium': return 'Média';
      case 'high': return 'Alta';
      case 'urgent': return 'Urgente';
      default: return priority;
    }
  };

  return (
    <div className="container mx-auto py-6">
      <Helmet>
        <title>CSM | Trivium - Tickets de Suporte</title>
      </Helmet>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold">Tickets de Suporte</h1>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Criar Ticket
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Lista de tickets */}
        <div className="col-span-12 lg:col-span-5">
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle>Tickets</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" title="Atualizar">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-8">
                    <Filter className="h-4 w-4 mr-1.5" />
                    Filtrar
                  </Button>
                </div>
              </div>
              <div className="mt-2 relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Pesquisar tickets..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9"
                />
              </div>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TicketStatus | 'all')}>
                <TabsList className="grid grid-cols-4 mb-4">
                  <TabsTrigger value="all">Todos</TabsTrigger>
                  <TabsTrigger value="new">Novos</TabsTrigger>
                  <TabsTrigger value="open">Abertos</TabsTrigger>
                  <TabsTrigger value="pending">Pendentes</TabsTrigger>
                </TabsList>
                
                <TabsContent value="all" className="m-0">
                  <TicketList 
                    tickets={filteredTickets} 
                    selectedTicketId={selectedTicket?.id}
                    onSelectTicket={handleSelectTicket}
                    getStatusBadgeVariant={getStatusBadgeVariant}
                    getPriorityBadgeVariant={getPriorityBadgeVariant}
                    getStatusLabel={getStatusLabel}
                    getPriorityLabel={getPriorityLabel}
                    contacts={contacts}
                    loading={loading}
                  />
                </TabsContent>
                
                {/* Tabs para cada status */}
                <TabsContent value="new" className="m-0">
                  <TicketList 
                    tickets={filteredTickets} 
                    selectedTicketId={selectedTicket?.id}
                    onSelectTicket={handleSelectTicket}
                    getStatusBadgeVariant={getStatusBadgeVariant}
                    getPriorityBadgeVariant={getPriorityBadgeVariant}
                    getStatusLabel={getStatusLabel}
                    getPriorityLabel={getPriorityLabel}
                    contacts={contacts}
                    loading={loading}
                  />
                </TabsContent>
                
                <TabsContent value="open" className="m-0">
                  <TicketList 
                    tickets={filteredTickets} 
                    selectedTicketId={selectedTicket?.id}
                    onSelectTicket={handleSelectTicket}
                    getStatusBadgeVariant={getStatusBadgeVariant}
                    getPriorityBadgeVariant={getPriorityBadgeVariant}
                    getStatusLabel={getStatusLabel}
                    getPriorityLabel={getPriorityLabel}
                    contacts={contacts}
                    loading={loading}
                  />
                </TabsContent>
                
                <TabsContent value="pending" className="m-0">
                  <TicketList 
                    tickets={filteredTickets} 
                    selectedTicketId={selectedTicket?.id}
                    onSelectTicket={handleSelectTicket}
                    getStatusBadgeVariant={getStatusBadgeVariant}
                    getPriorityBadgeVariant={getPriorityBadgeVariant}
                    getStatusLabel={getStatusLabel}
                    getPriorityLabel={getPriorityLabel}
                    contacts={contacts}
                    loading={loading}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Detalhes do ticket */}
        <div className="col-span-12 lg:col-span-7">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              {selectedTicket ? (
                <Card className="shadow-sm">
                  <CardHeader className="border-b bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="text-xl">{selectedTicket.subject}</CardTitle>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>Ticket #{selectedTicket.id.split('-')[1]}</span>
                          <span>•</span>
                          <span>{format(new Date(selectedTicket.created_at), 'dd/MM/yyyy HH:mm')}</span>
                          {selectedTicket.sla_breach && (
                            <Badge variant="destructive" className="ml-1">SLA violado</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {selectedTicket.status !== 'solved' && selectedTicket.status !== 'closed' && (
                          <Button onClick={handleResolveTicket}>
                            <CheckCircle className="h-4 w-4 mr-1.5" />
                            Resolver
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="p-4 border-b">
                      <div className="flex justify-between mb-4">
                        <div>
                          <h3 className="text-sm font-extrabold text-muted-foreground mb-1">Cliente</h3>
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                              {getContactById(selectedTicket.contact_id)?.name?.[0] || 'C'}
                            </div>
                            <div>
                              <p className="font-extrabold">{getContactById(selectedTicket.contact_id)?.name || 'Cliente'}</p>
                              <p className="text-sm text-muted-foreground">{getContactById(selectedTicket.contact_id)?.email || 'cliente@email.com'}</p>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <h3 className="text-sm font-extrabold text-muted-foreground mb-1">Categoria</h3>
                          <Badge variant="outline" className="px-2.5 py-0.5">
                            {selectedTicket.category.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h3 className="text-sm font-extrabold mb-2">Descrição</h3>
                        <div className="p-3 bg-muted rounded-md">
                          <p>{selectedTicket.description}</p>
                        </div>
                      </div>

                      {selectedTicket.resolution && (
                        <div className="mb-6">
                          <h3 className="text-sm font-extrabold mb-2">Resolução</h3>
                          <div className="p-3 bg-green-50 border border-green-100 rounded-md">
                            <p>{selectedTicket.resolution}</p>
                            {selectedTicket.resolution_date && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Resolvido em {format(new Date(selectedTicket.resolution_date), 'dd/MM/yyyy HH:mm')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-4 border-b">
                      <h3 className="text-sm font-extrabold mb-3">Adicionar Nota</h3>
                      <div className="space-y-2">
                        <textarea
                          placeholder="Adicione uma nota..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          className="w-full p-3 min-h-[100px] rounded-md border resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="private-note"
                              checked={isPrivateNote}
                              onChange={(e) => setIsPrivateNote(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <label htmlFor="private-note" className="text-sm">Nota privada (somente para equipe)</label>
                          </div>
                          <Button onClick={handleAddNote} size="sm">Adicionar Nota</Button>
                        </div>
                      </div>
                    </div>

                    <div className="p-4">
                      <h3 className="text-sm font-extrabold mb-3">Notas e Atividades</h3>
                      {loadingNotes ? (
                        <div className="text-center py-4">
                          <p className="text-sm text-muted-foreground">Carregando notas...</p>
                        </div>
                      ) : ticketNotes.length > 0 ? (
                        <div className="space-y-3">
                          {ticketNotes.map(note => (
                            <div key={note.id} className={`p-3 rounded-md ${note.is_private ? 'bg-blue-50 border border-blue-100' : 'border'}`}>
                              <div className="flex justify-between items-start">
                                <div className="flex items-start gap-2">
                                  <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 shrink-0 mt-0.5">
                                    {note.author_id === 'system' ? 'S' : 'A'}
                                  </div>
                                  <div>
                                    <p className="font-extrabold text-sm">
                                      {note.author_id === 'system' ? 'Sistema' : 'John Smith'}
                                      {note.is_private && (
                                        <Badge variant="outline" className="ml-2 bg-blue-50">Privado</Badge>
                                      )}
                                    </p>
                                    <p className="mt-1">{note.content}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {format(new Date(note.created_at), 'dd/MM/yyyy HH:mm')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">Nenhuma nota ainda</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="shadow-sm">
                  <div className="h-[700px] flex items-center justify-center">
                    <div className="text-center">
                      <h3 className="text-xl font-extrabold mb-2">Selecione um ticket</h3>
                      <p className="text-muted-foreground">Escolha um ticket da lista para ver os detalhes</p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
            
            {/* Painel lateral de propriedades do ticket */}
            <div className="md:col-span-1">
              {selectedTicket && (
                <TicketProperties 
                  ticket={selectedTicket} 
                  onUpdateStatus={handleUpdateStatus}
                  onUpdatePriority={handleUpdatePriority}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente de lista de tickets
interface TicketListProps {
  tickets: SupportTicket[];
  selectedTicketId?: string;
  onSelectTicket: (ticket: SupportTicket) => void;
  getStatusBadgeVariant: (status: TicketStatus) => BadgeVariant;
  getPriorityBadgeVariant: (priority: TicketPriority) => BadgeVariant;
  getStatusLabel: (status: TicketStatus) => string;
  getPriorityLabel: (priority: TicketPriority) => string;
  contacts: SupportContact[];
  loading: boolean;
}

const TicketList = ({ 
  tickets, 
  selectedTicketId, 
  onSelectTicket, 
  getStatusBadgeVariant,
  getPriorityBadgeVariant,
  getStatusLabel,
  getPriorityLabel,
  contacts,
  loading
}: TicketListProps) => {
  const getContactName = (contactId: string) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact?.name || 'Cliente';
  };
  
  if (loading) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Carregando tickets...</p>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Nenhum ticket encontrado</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto max-h-[600px]">
      <div className="border rounded-md overflow-hidden">
        {tickets.map((ticket) => (
          <div 
            key={ticket.id}
            className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors
              ${selectedTicketId === ticket.id ? 'bg-gray-100' : ''}`}
            onClick={() => onSelectTicket(ticket)}
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <h3 className="font-extrabold truncate mb-1">
                  {ticket.subject}
                </h3>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge variant={getStatusBadgeVariant(ticket.status)} className="font-extrabold">
                    {getStatusLabel(ticket.status)}
                  </Badge>
                  <Badge variant={getPriorityBadgeVariant(ticket.priority)} className="font-extrabold">
                    {getPriorityLabel(ticket.priority)}
                  </Badge>
                  {ticket.sla_breach && (
                    <Badge variant="destructive" className="font-extrabold">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      SLA
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                  <span className="flex items-center">
                    <User className="h-3 w-3 mr-1" />
                    {getContactName(ticket.contact_id)}
                  </span>
                  <span>•</span>
                  <span className="flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SupportTickets; 